/**
 * Survey → drawings: one single-line diagram per board (the board generator, continuation
 * sheets included) and one position plan per floor, with each room as a rectangle holding its
 * points, tagged like the diagram (A1, A2…). Generated sheets carry `source.kind = 'survey'`
 * and the survey signature; generating again replaces them (keeping their ids, so multi-line
 * diagrams made from them only go out of date).
 */
import {
  applyTransaction,
  boardCircuits,
  circuitPoints,
  newId,
  nextCircuitCode,
  roomPoints,
  sheetsInOrder,
  surveyBoards,
  surveyRooms,
  surveySignature,
  type Element,
  type Id,
  type Op,
  type Project,
  type PropValue,
  type Sheet,
  type SurveyBoard,
  type SurveyCircuit,
  type SurveyPoint,
  type SurveyRoom,
  type Locale,
} from '@elec/domain';
import { generateBoardOps, type BoardSpec, type CircuitSpec, type GroupSpec } from '@elec/editor';
import { paperSize } from '@elec/layout';
import { getSymbol } from '@elec/symbols';

export interface GenerateLabels {
  singleLine(board: string): string;
  plan(level: string): string;
  /** Name of a floor left empty. */
  defaultLevel: string;
}

export const LABELS: Record<Locale, GenerateLabels> = {
  fr: {
    singleLine: (b) => (b ? `Schéma unifilaire – ${b}` : 'Schéma unifilaire'),
    plan: (l) => `Plan de position – ${l}`,
    defaultLevel: 'Rez-de-chaussée',
  },
  en: {
    singleLine: (b) => (b ? `Single-line diagram – ${b}` : 'Single-line diagram'),
    plan: (l) => `Position plan – ${l}`,
    defaultLevel: 'Ground floor',
  },
  nl: {
    singleLine: (b) => (b ? `Eendraadschema – ${b}` : 'Eendraadschema'),
    plan: (l) => `Situatieschema – ${l}`,
    defaultLevel: 'Gelijkvloers',
  },
  de: {
    singleLine: (b) => (b ? `Einliniendiagramm – ${b}` : 'Einliniendiagramm'),
    plan: (l) => `Lageplan – ${l}`,
    defaultLevel: 'Erdgeschoss',
  },
};

/** Sheets generated from the survey. */
export const surveySheets = (p: Project): Sheet[] =>
  sheetsInOrder(p).filter((s) => s.source?.kind === 'survey');

/** The survey changed since the diagrams were generated (false when none were). */
export function surveyStale(p: Project): boolean {
  const sheets = surveySheets(p);
  if (!sheets.length) return false;
  const sig = surveySignature(p);
  return sheets.some((s) => s.source!.signature !== sig);
}

/** Circuits (records) drawn on the generated sheets. */
function generatedCircuitIds(p: Project, sheetIds: Set<Id>): Set<Id> {
  const out = new Set<Id>();
  for (const e of Object.values(p.elements)) if (sheetIds.has(e.sheetId) && e.circuitId) out.add(e.circuitId);
  return out;
}

/** Boards that produce a diagram: those with at least one circuit. */
const drawnBoards = (p: Project) => surveyBoards(p).filter((b) => boardCircuits(p, b.id).length > 0);

/**
 * Circuit letters the survey circuits will get on the diagram (A, B… in board order, after
 * the letters already used by circuits drawn by hand).
 */
export function surveyCodes(p: Project): Map<Id, string> {
  const gen = generatedCircuitIds(p, new Set(surveySheets(p).map((s) => s.id)));
  let working: Project = {
    ...p,
    circuits: Object.fromEntries(Object.entries(p.circuits).filter(([id]) => !gen.has(id))),
  };
  const out = new Map<Id, string>();
  for (const b of drawnBoards(p))
    for (const c of boardCircuits(p, b.id)) {
      const code = nextCircuitCode(working);
      out.set(c.id, code);
      working = {
        ...working,
        circuits: {
          ...working.circuits,
          [c.id]: { id: c.id, code, label: '', cable: { type: '', sectionMm2: 0, cores: 3 }, legacy: false },
        },
      };
    }
  return out;
}

/** One point of the diagram per unit of quantity. */
const units = (pts: SurveyPoint[]) =>
  pts.flatMap((pt) => Array.from({ length: Math.max(1, pt.quantity) }, () => pt));

function circuitSpec(p: Project, c: SurveyCircuit): CircuitSpec {
  const props: Record<string, PropValue> = {};
  if (c.ratingA) props.ratingA = c.ratingA;
  if (c.curve && c.device !== 'fuse') props.curve = c.curve;
  if (c.poles) props.poles = c.poles;
  return {
    label: c.label,
    device: {
      symbolId: c.device === 'RCBO' ? 'iec.rcbo' : c.device === 'fuse' ? 'iec.fuse' : 'iec.breaker',
      props,
    },
    points: units(circuitPoints(p, c.id))
      .filter((pt) => getSymbol(pt.symbolId)?.views.includes('single-line'))
      .map((pt) => ({ symbolId: pt.symbolId, props: { ...pt.props } })),
    ...(c.cable ? { cable: { ...c.cable } } : {}),
    ...(c.legacy ? { legacy: true } : {}),
  };
}

/** The board structure for the generator, and the survey circuits in the order they are drawn. */
export function boardSpecOf(p: Project, board: SurveyBoard): { spec: BoardSpec; order: SurveyCircuit[] } {
  const circuits = boardCircuits(p, board.id);
  const keys = new Set(board.rcds.map((r) => r.key));
  const main: GroupSpec = { circuits: [] };
  const order: SurveyCircuit[] = [];
  for (const c of circuits.filter((c) => !c.rcdKey || !keys.has(c.rcdKey))) {
    main.circuits.push(circuitSpec(p, c));
    order.push(c);
  }
  const groups: GroupSpec[] = [main];
  for (const r of board.rcds) {
    const cs = circuits.filter((c) => c.rcdKey === r.key);
    if (!cs.length) continue;
    groups.push({
      rcd: { sensitivityMa: r.sensitivityMa, rcdType: r.rcdType, ratingA: r.ratingA },
      circuits: cs.map((c) => circuitSpec(p, c)),
    });
    order.push(...cs);
  }
  return {
    spec: {
      ...(board.main ? { main: { ...board.main } } : {}),
      ...(board.mainRcd ? { mainRcd: { ...board.mainRcd } } : {}),
      groups: groups.filter((g) => g.circuits.length),
    },
    order,
  };
}

// ---------- position plans ----------

const PER_ROW = 5;
const STEP = 15;
const ROOM_W = PER_ROW * STEP + 10;
const GAP = 8;

interface PlacedRoom {
  room: SurveyRoom;
  x: number;
  y: number;
  w: number;
  h: number;
  cells: SurveyPoint[];
}

const roomHeight = (n: number) => 14 + Math.max(1, Math.ceil(n / PER_ROW)) * STEP + 4;

/** Flow the rooms of one floor onto pages of the drawing area. */
function layoutRooms(p: Project, rooms: SurveyRoom[], area: { x: number; y: number; w: number; h: number }) {
  const pages: PlacedRoom[][] = [[]];
  let x = area.x;
  let y = area.y;
  let rowH = 0;
  for (const room of rooms) {
    const cells = units(
      roomPoints(p, room.id).filter((pt) => getSymbol(pt.symbolId)?.views.includes('position-plan')),
    );
    const h = Math.min(roomHeight(cells.length), area.h);
    if (x + ROOM_W > area.x + area.w) {
      x = area.x;
      y += rowH + GAP;
      rowH = 0;
    }
    if (y + h > area.y + area.h && pages.at(-1)!.length) {
      pages.push([]);
      x = area.x;
      y = area.y;
      rowH = 0;
    }
    pages.at(-1)!.push({ room, x, y, w: ROOM_W, h, cells });
    x += ROOM_W + GAP;
    rowH = Math.max(rowH, h);
  }
  return pages;
}

export interface GenerateResult {
  ops: Op[];
  /** Survey circuit → circuit letter on the diagram. */
  codes: Map<Id, string>;
  sheetIds: Id[];
}

/** Generate (or regenerate) every diagram of the survey. */
export function generateFromSurvey(project: Project, labels: GenerateLabels): GenerateResult {
  const ops: Op[] = [];
  let w = project;
  const push = (more: Op[]) => {
    if (!more.length) return;
    w = applyTransaction(w, { label: '', ops: more }).project;
    ops.push(...more);
  };
  const signature = surveySignature(project);

  // 1. Remove the previous generated sheets, their contents and their circuits; remember their
  //    ids and places so the new sheets take them over.
  const previous = surveySheets(project);
  const prevIds = new Set(previous.map((s) => s.id));
  const reuse = new Map<string, Sheet>();
  for (const s of previous) {
    const key = s.kind === 'single-line' ? `board:${s.source!.fromSheetId}` : `plan:${s.source!.level ?? ''}`;
    if (!s.source!.part || s.source!.part === 1) if (!reuse.has(key)) reuse.set(key, s);
  }
  const removal: Op[] = [];
  for (const wire of Object.values(project.wires))
    if (prevIds.has(wire.sheetId)) removal.push({ op: 'remove', coll: 'wires', id: wire.id });
  for (const e of Object.values(project.elements))
    if (prevIds.has(e.sheetId)) removal.push({ op: 'remove', coll: 'elements', id: e.id });
  for (const id of generatedCircuitIds(project, prevIds))
    removal.push({ op: 'remove', coll: 'circuits', id });
  for (const id of prevIds) removal.push({ op: 'remove', coll: 'sheets', id });
  // First generation in a new project: the first board takes over its empty default sheet,
  // rather than leaving a blank page in front of the diagrams.
  const first = drawnBoards(project)[0];
  if (!previous.length && first) {
    const blank = sheetsInOrder(project).find(
      (s) =>
        s.kind === 'single-line' &&
        !s.source &&
        !Object.values(project.elements).some((e) => e.sheetId === s.id),
    );
    if (blank) {
      reuse.set(`board:${first.id}`, blank);
      removal.push({ op: 'remove', coll: 'sheets', id: blank.id });
    }
  }
  push(removal);

  let order = Math.max(-1, ...Object.values(w.sheets).map((s) => s.order)) + 1;
  const newSheet = (key: string, init: Omit<Sheet, 'id' | 'order'>): Sheet => {
    const old = reuse.get(key);
    const sheet: Sheet = { ...init, id: old?.id ?? newId(), order: old?.order ?? order++ };
    push([{ op: 'add', coll: 'sheets', value: sheet }]);
    return sheet;
  };

  // 2. Single-line diagram of each board.
  const codes = new Map<Id, string>();
  const tags = new Map<Id, string[]>(); // survey point → tags of its units
  const sheetIds: Id[] = [];
  const boards = drawnBoards(project);
  for (const board of boards) {
    const { spec, order: drawn } = boardSpecOf(project, board);
    const name = labels.singleLine(boards.length > 1 ? board.name : '');
    const sheet = newSheet(`board:${board.id}`, {
      name,
      kind: 'single-line',
      paper: 'A4',
      orientation: 'landscape',
      source: { kind: 'survey', fromSheetId: board.id, signature },
      ...(board.blankingPlates ? { blankingPlates: board.blankingPlates } : {}),
    });
    const before = new Set(Object.keys(w.sheets));
    const gen = generateBoardOps(w, sheet.id, spec);
    push(gen);
    // Circuit records are added in drawing order: match them with the survey circuits.
    const added = gen.filter(
      (o): o is Op & { op: 'add'; coll: 'circuits' } => o.op === 'add' && o.coll === 'circuits',
    );
    added.forEach((o, i) => {
      const c = drawn[i];
      if (!c) return;
      codes.set(c.id, o.value.code);
      let n = 0;
      for (const pt of units(circuitPoints(project, c.id))) {
        if (!getSymbol(pt.symbolId)?.views.includes('single-line')) continue;
        const list = tags.get(pt.id) ?? [];
        list.push(`${o.value.code}${++n}`);
        tags.set(pt.id, list);
      }
    });
    sheetIds.push(sheet.id);
    // The earth electrode and its measured resistance, next to the supply of the first board.
    if (board === boards[0]) {
      const ohm = boards.map((b) => b.earthOhm).find((v) => v !== undefined);
      push([
        {
          op: 'add',
          coll: 'elements',
          value: {
            id: newId(),
            sheetId: sheet.id,
            symbolId: 'iec.earth-electrode',
            x: 12,
            y: 70,
            rotation: 0,
            mirrored: false,
            tag: 'PE',
            props: ohm !== undefined ? { resistanceOhm: ohm } : {},
          },
        },
      ]);
    }
    // Continuation sheets made by the generator are survey sheets too.
    let part = 1;
    for (const s of sheetsInOrder(w))
      if (!before.has(s.id)) {
        sheetIds.push(s.id);
        push([
          {
            op: 'update',
            coll: 'sheets',
            id: s.id,
            patch: { source: { kind: 'survey', fromSheetId: board.id, signature, part: ++part } },
          },
        ]);
      }
  }

  // 3. Position plan of each floor.
  const levels: string[] = [];
  for (const r of surveyRooms(project)) {
    const l = r.level.trim() || labels.defaultLevel;
    if (!levels.includes(l)) levels.push(l);
  }
  for (const level of levels) {
    const rooms = surveyRooms(project).filter((r) => (r.level.trim() || labels.defaultLevel) === level);
    const proto = { paper: 'A3' as const, orientation: 'landscape' as const };
    const { w: pw, h: ph } = paperSize(proto);
    const area = { x: 20, y: 28, w: pw - 40, h: ph - 28 - 50 };
    const pages = layoutRooms(project, rooms, area);
    pages.forEach((page, pi) => {
      const sheet = newSheet(pi === 0 ? `plan:${level}` : `plan:${level}:${pi + 1}`, {
        name: pages.length > 1 ? `${labels.plan(level)} (${pi + 1})` : labels.plan(level),
        kind: 'position-plan',
        ...proto,
        planScale: 50,
        source: { kind: 'survey', fromSheetId: '', signature, level, ...(pi ? { part: pi + 1 } : {}) },
      });
      sheetIds.push(sheet.id);
      const els: Op[] = [];
      for (const r of page) {
        els.push({
          op: 'add',
          coll: 'elements',
          value: {
            id: newId(),
            sheetId: sheet.id,
            symbolId: 'plan.room',
            x: r.x,
            y: r.y,
            rotation: 0,
            mirrored: false,
            tag: '',
            props: { name: r.room.name, roomType: r.room.roomType, w: r.w, h: r.h },
          },
        });
        const used = new Map<Id, number>();
        r.cells.forEach((pt, i) => {
          const k = used.get(pt.id) ?? 0;
          used.set(pt.id, k + 1);
          const el: Element = {
            id: newId(),
            sheetId: sheet.id,
            symbolId: pt.symbolId,
            x: r.x + 7.5 + (i % PER_ROW) * STEP,
            y: r.y + 14 + Math.floor(i / PER_ROW) * STEP,
            rotation: 0,
            mirrored: false,
            tag: tags.get(pt.id)?.[k] ?? '',
            props: { ...pt.props },
          };
          // Cells that do not fit in a (page-high) room are left out rather than drawn outside.
          if (el.y + 10 <= r.y + r.h) els.push({ op: 'add', coll: 'elements', value: el });
        });
      }
      push(els);
    });
  }
  return { ops, codes, sheetIds };
}
